import {openDialog, Parameter, Parameters} from 'platform/components';
import {HStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleClassesQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, isInvalidEmail} from '@dms/shared';
import {SendAfterSalesEmailForm, SendAfterSalesCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {ServiceCaseOrder} from '../../../../../types/OrderType';

interface OrderInformationProps extends RequiredTestIdProps {
  serviceOrder: ServiceCaseOrder;
  customerContractName?: string | Nullish;
  isReadOnly?: boolean;
}

export function OrderInformation(props: OrderInformationProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const formatDateTime = useDateTimeFormatter();

  const vehicleClassId = props.serviceOrder?.vehicleClassId;
  // One tenant-wide code list shared by every order card through the RTK cache, rather than a
  // per-order assignment and name request.
  const {data: vehicleClasses} = useGetVehicleClassesQuery(undefined, {
    skip: isNilOrEmpty(vehicleClassId),
  });
  const vehicleClassName = vehicleClasses?.find(
    (vehicleClass) => vehicleClass.vehicleClassId === vehicleClassId
  )?.name;

  const checkoutCustomerName =
    props.serviceOrder?.billingInformation?.customerInformation?.displayName;
  const contactName = props.serviceOrder?.contactInformation?.name;
  const contactPersonName = isNotNilOrEmpty(contactName) ? contactName : checkoutCustomerName;
  const emailExists = isNotNilOrEmpty(props.serviceOrder?.contactInformation?.email);
  const isEmailInvalid = isInvalidEmail(props.serviceOrder?.contactInformation?.email);
  const phoneNumberExists = isNotNilOrEmpty(
    props.serviceOrder?.contactInformation?.phoneNumber?.number
  );
  const emailTooltip = [
    contactPersonName,
    isEmailInvalid
      ? i18n.t('general.validations.invalidEmail')
      : i18n.t('general.actions.sendEmail'),
  ]
    .filter(isNotNilOrEmpty)
    .join(' · ');
  const phoneTooltip = [contactPersonName, i18n.t('general.actions.sendSms')]
    .filter(isNotNilOrEmpty)
    .join(' · ');

  const openEmailForm = () => {
    openDialog(
      <SendAfterSalesEmailForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.serviceOrder?.serviceOrderId ?? ''}
        defaultValues={{defaultCategory: 'customer-message'}}
      />,
      {
        id: 'email-sms-notification-dialog',
        withAdditionalFooter: true,
        size: 'large',
        title: i18n.t('general.actions.sendEmail'),
      }
    );
  };

  const openSmsForm = () => {
    openDialog(
      <SendAfterSalesCustomerSmsForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.serviceOrder?.serviceOrderId ?? ''}
        recordId={props.serviceOrder?.serviceOrderId ?? ''}
        resourceId={EntityResourceIds.serviceOrder}
      />,
      {
        title: i18n.t('general.actions.sendSms'),
        size: 'large',
        scrollBehavior: 'outside',
      }
    );
  };

  const leftParameters = buildArray<Parameter>()
    .add(() => {
      const dateInfo = match(props.serviceOrder?.state)
        .with('CLOSED', () => ({
          tooltip: i18n.t('entity.order.labels.closedAt'),
          value: props.serviceOrder?.orderClosedDate
            ? formatDateTime('dateTimeMedium', parseDate(props.serviceOrder?.orderClosedDate))
            : EMPTY_PLACEHOLDER,
        }))
        .with('CANCELLED', () => ({
          tooltip: i18n.t('entity.order.labels.cancelledAt'),
          value: props.serviceOrder?.orderCancelledDate
            ? formatDateTime('dateTimeMedium', parseDate(props.serviceOrder?.orderCancelledDate))
            : EMPTY_PLACEHOLDER,
        }))
        .otherwise(() => ({
          tooltip: i18n.t('general.labels.completion'),
          value: props.serviceOrder?.estimatedCompletionDate
            ? formatDateTime(
                'dateTimeMedium',
                parseDate(props.serviceOrder?.estimatedCompletionDate)
              )
            : i18n.t('entity.order.labels.noCompletionDate'),
        }));
      return {
        title: dateInfo.value,
        tooltip: dateInfo.tooltip,
        icon: 'action/date_range',
        variant: 'secondary',
      };
    })
    .add({
      title: isNotNilOrEmpty(checkoutCustomerName)
        ? checkoutCustomerName
        : i18n.t('entity.order.contactInformation.noInvoiceRecipient'),
      tooltip: i18n.t('entity.invoice.labels.recipientName'),
      icon: 'action/receipt',
      variant: 'secondary',
    })
    .add({
      title: isNotNilOrEmpty(props.customerContractName)
        ? props.customerContractName
        : i18n.t('entity.order.contactInformation.noContract'),
      tooltip: i18n.t('entity.order.labels.appliedCustomerContract'),
      icon: 'editor/monetization_on',
      variant: 'secondary',
    })
    .when(isNotNilOrEmpty(vehicleClassName), {
      title: vehicleClassName,
      tooltip: i18n.t('entity.vehicleClass.labels.vehicleClass'),
      icon: 'automotive/car',
      variant: 'secondary',
    })
    .when(isNotNilOrEmpty(props.serviceOrder?.claimDecision), {
      title: i18n.t(`entity.order.labels.decisionOnClaim.${props.serviceOrder?.claimDecision}`),
      tooltip: i18n.t('entity.order.labels.decisionStatus'),
      icon: 'social/notifications',
      variant: 'secondary',
    });

  const rightParameters = buildArray<Parameter>()
    .add({
      title: emailExists
        ? props.serviceOrder?.contactInformation?.email
        : i18n.t('entity.customer.labels.noEmail'),
      tooltip: emailTooltip,
      icon: isEmailInvalid ? 'alert/warning' : 'communication/email',
      variant: 'link',
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        openEmailForm();
      },
    })
    .add({
      title: phoneNumberExists
        ? formatPhoneNumber(
            `${props.serviceOrder?.contactInformation?.phoneNumber?.prefix ?? ''}${props.serviceOrder?.contactInformation?.phoneNumber?.number ?? ''}`
          )
        : i18n.t('entity.customer.labels.noPhoneNumber'),
      tooltip: phoneTooltip,
      icon: 'communication/phone',
      variant: 'link',
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        openSmsForm();
      },
    });

  return (
    <HStack align="flex-start" justify="space-between">
      <Parameters
        parameters={leftParameters}
        data-testid={suffixTestId('contactInformation-left', props)}
        size="small"
      />
      <Parameters
        parameters={rightParameters}
        data-testid={suffixTestId('contactInformation-right', props)}
        size="small"
      />
    </HStack>
  );
}
